// pages/SocialMedia.jsx
import { useNavigate } from 'react-router-dom';

export default function SocialMedia() {
  const navigate = useNavigate();

  const platforms = [
    { name: "Facebook", icon: "📘", reach: "2.9B users" },
    { name: "Instagram", icon: "📸", reach: "2B users" },
    { name: "TikTok", icon: "🎵", reach: "1B users" },
    { name: "LinkedIn", icon: "💼", reach: "900M users" },
    { name: "Twitter/X", icon: "🐦", reach: "550M users" },
    { name: "Snapchat", icon: "👻", reach: "750M users" }
  ];

  const features = [
    {
      icon: "🎯",
      title: "Unified Campaign Management",
      description: "Create and manage campaigns across all social platforms from one dashboard."
    },
    {
      icon: "📊",
      title: "Cross-Platform Analytics",
      description: "Compare performance across networks to optimize your social strategy."
    },
    {
      icon: "🤖",
      title: "AI-Powered Targeting",
      description: "Reach the right audience with machine learning-driven segmentation."
    },
    {
      icon: "⚡",
      title: "Instant Publishing",
      description: "Schedule and publish content across all platforms simultaneously."
    },
    {
      icon: "📱",
      title: "Format Optimization",
      description: "Automatically adapt creative for each platform's specifications."
    },
    {
      icon: "💬",
      title: "Social Listening",
      description: "Monitor brand mentions and engage with your audience in real-time."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Social Media Advertising
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Build, personalize, and optimize creative for every major social platform.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Launch Campaign
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                See Examples
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Supported Platforms
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {platforms.map((platform, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                  {platform.icon}
                </div>
                <div className="font-semibold mb-1">{platform.name}</div>
                <div className="text-xs text-gray-400">{platform.reach}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Everything You Need for Social
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Dominate Social Media Advertising
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Reach billions of users across every major platform.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Get Started Free
          </button>
        </div>
      </section>
    </div>
  );
}
